import { Dropdown, Tooltip } from 'antd';
import { NavLink } from 'react-router-dom';
import { 
  SettingOutlined, 
  UserOutlined, 
  TeamOutlined, 
  ShoppingOutlined, 
  ShoppingCartOutlined, 
  CreditCardOutlined, 
  LogoutOutlined 
} from '@ant-design/icons';

import { imageUrl } from '@common';
import { useAuth, useOrders } from '@hooks';
import './styles.css';

interface UserMenuProps {
  profile?: any;
}

export const UserMenu = ({ profile }: UserMenuProps) => {
  const { logout } = useAuth();
  const { createPortalSession } = useOrders();

  const hasStripeCustomer = !!profile?.stripeCustomerId;

  const handleAccessBillingPortal = async () => {
    if (!hasStripeCustomer) return;
    try {
      // Get current URL to return to after billing portal
      const currentUrl = window.location.href;
      await createPortalSession.mutateAsync(currentUrl);
      // Redirect is handled by the mutation's onSuccess callback
    } catch (error) {
      // Error handled by hook
    }
  };

  const avatar = profile?.physicalPerson?.avatar;
  const name = profile?.physicalPerson?.name;
  const email = profile?.email;

  const getInitial = () => {
    if (name) return name.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  };

  return (
    <div className='flex'>
      <div className='w-10 h-10'>
        <Dropdown
          overlayClassName="user-menu-dropdown"
          menu={{
            items: [
              // Account Section
              {
                key: 'account-header',
                label: <div className="user-menu-section-header font-android tracking-wide">Account</div>,
                disabled: true,
              },
              {
                key: 'settings',
                icon: <SettingOutlined className="user-menu-icon" />,
                label: <NavLink to='/app/configuracoes' className="user-menu-item">Settings</NavLink>,
              },
              // Admin Section - only show if user is admin
              ...(profile?.profile === 'admin' ? [
                {
                  type: 'divider' as const,
                },
                {
                  key: 'admin-header',
                  label: <div className="user-menu-section-header font-android tracking-wide">Administration</div>,
                  disabled: true,
                },
                {
                  key: 'users',
                  icon: <UserOutlined className="user-menu-icon" />,
                  label: <NavLink to='/users' className="user-menu-item">User Management</NavLink>,
                },
                {
                  key: 'talent-pool',
                  icon: <TeamOutlined className="user-menu-icon" />,
                  label: <NavLink to='/talent-pool' className="user-menu-item">Talent Pool</NavLink>,
                },
                {
                  type: 'divider' as const,
                },
                {
                  key: 'commerce-header',
                  label: <div className="user-menu-section-header font-android tracking-wide">Shop</div>,
                  disabled: true,
                },
                {
                  key: 'products',
                  icon: <ShoppingOutlined className="user-menu-icon" />,
                  label: <NavLink to='/app/produtos' className="user-menu-item">Products</NavLink>,
                },
                {
                  key: 'orders',
                  icon: <ShoppingCartOutlined className="user-menu-icon" />,
                  label: <NavLink to='/app/pedidos' className="user-menu-item">Orders</NavLink>,
                },
                {
                  key: 'billing',
                  icon: <CreditCardOutlined className="user-menu-icon" />,
                  disabled: !hasStripeCustomer,
                  label: hasStripeCustomer ? (
                    <div onClick={handleAccessBillingPortal} className="user-menu-item">
                      Billing Portal
                    </div>
                  ) : (
                    <Tooltip title="Available after your first purchase" placement="left">
                      <div className="text-gray-400 cursor-not-allowed user-menu-item">
                        Billing Portal
                      </div>
                    </Tooltip>
                  ),
                },
                // Testimonials route doesn't exist yet
                // {
                //   key: 'testimonials',
                //   label: <NavLink to='/testimonials'>Testimonials</NavLink>,
                // }
              ] : []),
              // Divider before logout
              {
                type: 'divider' as const,
              },
              // Logout Section
              {
                key: 'logout',
                icon: <LogoutOutlined className="user-menu-icon logout-icon" />,
                label: (
                  <div onClick={() => logout()} className='user-menu-item logout-item cursor-pointer'>
                    Logout
                  </div>
                ),
              },
            ],
          }}
          placement='bottomRight'
          trigger={['click']}
        >
          <div className='w-full h-full bg-zinc-900 rounded-none border-2 border-zinc-500 cursor-pointer overflow-hidden'>
            {avatar ? (
              <img
                src={imageUrl(avatar)}
                alt={name || email}
                className='w-full h-full object-cover'
              />
            ) : (
              <div className='w-full h-full flex items-center justify-center'>
                <div className='text-zinc-400 text-xl'>{getInitial()}</div>
              </div>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );
};
